import { useState, useEffect, useRef, useCallback, lazy, Suspense } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Drawer,
  DrawerContent,
} from '@/components/ui/drawer';
import { Button } from '@/components/ui/button';
import { X, Loader2, CreditCard, Check } from 'lucide-react';
import { useCurrencyDetection } from '@/hooks/use-currency-detection';
import { useAppConfig } from '@/hooks/use-app-config';
import { useAuth } from '@/components/AuthProvider';
import { useToast } from '@/hooks/use-toast';
import { useSubscriptionStatus } from '@/hooks/use-subscription-status';
import { supabase } from '@/integrations/supabase/client';
import { MediaUnlocker } from '@/lib/mediaUnlocker';
import { usePaymentRecoveryPolling } from '@/hooks/use-payment-recovery-polling';
import { useQueryClient } from '@tanstack/react-query';
import { useReadyPaymentRequest } from '@/hooks/use-ready-payment-request';
import { getCachedPublishableKey } from '@/lib/stripePreloader';

const StripeElementsPaymentForm = lazy(() => 
  import('@/components/subscription/StripeElementsPaymentForm').then(m => ({ default: m.StripeElementsPaymentForm }))
);
const PaymentSuccessAnimation = lazy(() => 
  import('@/components/PaymentSuccessAnimation').then(m => ({ default: m.PaymentSuccessAnimation }))
);

interface OnboardingPaymentSheetProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  hideCustomBook?: boolean;
}

type PlanTier = 'monthly' | 'annual';

export function OnboardingPaymentSheet({ 
  isOpen, 
  onClose, 
  onSuccess,
  hideCustomBook = false,
}: OnboardingPaymentSheetProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { toast } = useToast();
  const { data: subscriptionStatus } = useSubscriptionStatus();
  const [selectedTier, setSelectedTier] = useState<PlanTier>('annual');
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [applePayActive, setApplePayActive] = useState(false);
  const { currency, country } = useCurrencyDetection();
  const { data: appConfig } = useAppConfig();
  
  // Use pre-warmed Stripe for instant Apple Pay / Google Pay
  const {
    paymentRequest,
    canMakePayment,
    isReady: paymentMethodsReady,
    stripe: stripeInstance,
    publishableKey: stripePublishableKey,
    Elements: StripeElements,
    PaymentRequestButtonElement: PaymentRequestButton,
    updateAmount,
  } = useReadyPaymentRequest({ defaultTier: 'annual', enabled: isOpen });
  
  const handlerAttachedRef = useRef(false);
  const selectedTierRef = useRef<PlanTier>('annual');
  const [prefetchedClientSecret, setPrefetchedClientSecret] = useState<string | null>(null);
  const setupIntentFetchedRef = useRef(false);

  // Keep ref in sync
  useEffect(() => {
    selectedTierRef.current = selectedTier;
  }, [selectedTier]);

  const handleRecoverySuccess = useCallback(() => {
    console.log('[OnboardingPayment] Recovery polling detected subscription!');
    setApplePayActive(false);
    setShowSuccess(true);
    queryClient.invalidateQueries({ queryKey: ['subscriptionStatus'] });
    
    if (onSuccess) {
      onSuccess();
    } else {
      onClose();
      setTimeout(() => {
        navigate('/?payment_success=true', { replace: true });
      }, 1500);
    }
  }, [navigate, queryClient, onSuccess, onClose]);

  usePaymentRecoveryPolling({
    enabled: applePayActive,
    onSuccess: handleRecoverySuccess,
    pollInterval: 2000,
    maxDuration: 30000,
  });

  const annualPrice = appConfig?.annual_price || 95.88;
  const monthlyPrice = appConfig?.monthly_price || 9.99;
  const monthlyEquivalent = (annualPrice / 12).toFixed(2);
  const annualSavings = ((monthlyPrice * 12) - annualPrice).toFixed(0);
  
  const currentPrice = selectedTier === 'annual' ? annualPrice : monthlyPrice;

  // Pre-fetch SetupIntent
  useEffect(() => {
    if (!isOpen || !user?.email) return;
    if (setupIntentFetchedRef.current) return;
    
    setupIntentFetchedRef.current = true;
    supabase.functions.invoke('create-setup-intent', {
      body: { email: user.email }
    }).then(({ data, error }) => {
      if (!error && data?.client_secret) {
        setPrefetchedClientSecret(data.client_secret);
      }
    });
  }, [isOpen, user?.email]);

  // Handle tier change - update amount instantly
  const handleTierChange = (tier: PlanTier) => {
    setSelectedTier(tier);
    selectedTierRef.current = tier;
    updateAmount(tier);
  };

  // Pre-fetch SetupIntent
  useEffect(() => {
    if (!isOpen || !user?.email) return;
    if (setupIntentFetchedRef.current) return;
    
    setupIntentFetchedRef.current = true;
    supabase.functions.invoke('create-setup-intent', {
      body: { email: user.email }
    }).then(({ data, error }) => {
      if (!error && data?.client_secret) {
        setPrefetchedClientSecret(data.client_secret);
      }
    });
  }, [isOpen, user?.email]);


  const handlerRef = useRef<{
    user: typeof user;
    stripeInstance: any;
    currency: typeof currency;
    navigate: typeof navigate;
    toast: typeof toast;
    onClose: typeof onClose;
    onSuccess: typeof onSuccess;
    setIsProcessing: typeof setIsProcessing;
    setShowSuccess: typeof setShowSuccess;
  }>({
    user,
    stripeInstance,
    currency,
    navigate,
    toast,
    onClose,
    onSuccess,
    setIsProcessing,
    setShowSuccess,
  });

  useEffect(() => {
    handlerRef.current = {
      user,
      stripeInstance,
      currency,
      navigate,
      toast,
      onClose,
      onSuccess,
      setIsProcessing,
      setShowSuccess,
    };
  }, [user, stripeInstance, currency, navigate, toast, onClose, onSuccess]);

  // Attach payment handler
  useEffect(() => {
    if (!paymentRequest || handlerAttachedRef.current) return;
    handlerAttachedRef.current = true;

    const handlePaymentMethod = async (ev: any) => {
      console.log('[OnboardingPayment] Payment method event');
      const { user, stripeInstance, currency, navigate, toast, onClose, onSuccess, setIsProcessing, setShowSuccess } = handlerRef.current;
      const tier = selectedTierRef.current;
      
      if (!user || !stripeInstance) {
        try { ev.complete('fail'); } catch {}
        return;
      }

      setIsProcessing(true);
      setApplePayActive(true);
      
      try {
        MediaUnlocker.instance().unlock().catch(() => {});

        const { data, error } = await supabase.functions.invoke('create-setup-intent', {
          body: { email: user.email }
        });
        
        if (error || !data?.client_secret) {
          try { ev.complete('fail'); } catch {}
          throw new Error('Failed to create payment setup');
        }

        const { error: confirmError } = await stripeInstance.confirmCardSetup(
          data.client_secret,
          { payment_method: ev.paymentMethod.id },
          { handleActions: false }
        );

        if (confirmError) {
          try { ev.complete('fail'); } catch {}
          throw confirmError;
        }

        const { data: subData, error: subError } = await supabase.functions.invoke('start-trial-subscription', {
          body: {
            payment_method: ev.paymentMethod.id,
            tier: tier,
            currency: currency.code.toLowerCase()
          }
        });
        
        if (subError || !subData?.subscription_id) {
          try { ev.complete('fail'); } catch {}
          throw new Error(subData?.error || subError?.message || 'Subscription creation failed');
        }

        try { ev.complete('success'); } catch {}
        
        setApplePayActive(false);
        queryClient.invalidateQueries({ queryKey: ['subscriptionStatus'] });
        setShowSuccess(true);
        
        if (onSuccess) {
          onSuccess();
        } else {
          onClose();
          setTimeout(() => {
            navigate('/?payment_success=true', { replace: true });
          }, 1500);
        }
      } catch (err: any) {
        console.error('[OnboardingPayment] Payment error:', err);
        setApplePayActive(false);
        setIsProcessing(false);
        toast({
          title: "Payment failed",
          description: err.message || "Please try again",
          variant: "destructive",
        });
      }
    };

    paymentRequest.on('paymentmethod', handlePaymentMethod);
  }, [paymentRequest, queryClient]);

  const handlePaymentSuccess = () => {
    setShowPaymentForm(false);
    setShowSuccess(true);
    queryClient.invalidateQueries({ queryKey: ['subscriptionStatus'] });
    
    if (onSuccess) {
      onSuccess();
    } else {
      onClose();
      setTimeout(() => {
        navigate('/?payment_success=true', { replace: true });
      }, 1500);
    }
  };

  const content = (
    <div className="flex flex-col h-full bg-gradient-to-b from-background via-background to-primary/5">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-white/10">
        <div className="flex items-center gap-2">
          <img 
            src="https://hwjejrsktuenskryvsvl.supabase.co/storage/v1/object/public/applogos/buxbox_tiny.webp" 
            alt="BuxBox" 
            className="h-5 w-5"
          />
          <h2 className="text-lg font-semibold text-foreground">Choose Your Plan</h2>
        </div>
        <Button variant="ghost" size="icon" onClick={onClose} disabled={isProcessing} className="rounded-full hover:bg-white/10">
          <X className="h-5 w-5" />
        </Button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {showSuccess ? (
          <Suspense fallback={<div className="flex justify-center py-8"><Loader2 className="h-8 w-8 animate-spin" /></div>}>
            <PaymentSuccessAnimation />
          </Suspense>
        ) : showPaymentForm ? (
          // Show payment form - it handles its own loading state for Stripe
          <Suspense fallback={<div className="flex flex-col items-center justify-center py-16 gap-3"><Loader2 className="h-8 w-8 animate-spin text-muted-foreground" /><p className="text-sm text-muted-foreground">Loading payment form...</p></div>}>
            <StripeElementsPaymentForm
              tier={selectedTier}
              price={currentPrice}
              currencyCode={currency.code.toLowerCase()}
              currencySymbol={currency.symbol}
              stripePublishableKey={stripePublishableKey || getCachedPublishableKey() || ''}
              onBack={() => setShowPaymentForm(false)}
              onSuccess={handlePaymentSuccess}
              prefetchedClientSecret={prefetchedClientSecret}
            />
          </Suspense>
        ) : (
          <>
            {/* Plan Selection */}
            <div className="space-y-3">
              {/* Annual Plan Card */}
              <button
                onClick={() => handleTierChange('annual')}
                className={`relative w-full text-left rounded-2xl p-4 transition-all duration-300 ${
                  selectedTier === 'annual'
                    ? 'bg-gradient-to-br from-primary/20 via-primary/10 to-transparent border-2 border-primary shadow-lg shadow-primary/20'
                    : 'bg-white/5 border border-white/10 hover:border-white/20 hover:bg-white/10'
                } backdrop-blur-xl`}
              >
                {/* Best Value Badge */}
                <div className="absolute -top-3 left-4 bg-gradient-to-r from-primary to-primary/80 text-primary-foreground text-xs font-bold px-3 py-1 rounded-full shadow-lg">
                  BEST VALUE
                </div>
                
                <div className="flex items-center justify-between pt-2">
                  <div className="flex items-center gap-3">
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all ${
                      selectedTier === 'annual' ? 'border-primary bg-primary' : 'border-white/30'
                    }`}>
                      {selectedTier === 'annual' && <Check className="h-3 w-3 text-primary-foreground" />}
                    </div>
                    <div>
                      <h3 className="text-base font-bold text-foreground">Annual</h3>
                      <p className="text-xs text-muted-foreground">Billed yearly</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-xl font-bold text-foreground">
                      {currency.symbol}{monthlyEquivalent}
                      <span className="text-sm font-normal text-muted-foreground">/mo</span>
                    </div>
                    <div className="text-xs text-primary font-medium">
                      Save {currency.symbol}{annualSavings}/year
                    </div>
                  </div>
                </div>
              </button>

              {/* Monthly Plan Card */}
              <button
                onClick={() => handleTierChange('monthly')}
                className={`relative w-full text-left rounded-2xl p-4 transition-all duration-300 ${
                  selectedTier === 'monthly'
                    ? 'bg-gradient-to-br from-primary/20 via-primary/10 to-transparent border-2 border-primary shadow-lg shadow-primary/20'
                    : 'bg-white/5 border border-white/10 hover:border-white/20 hover:bg-white/10'
                } backdrop-blur-xl`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all ${
                      selectedTier === 'monthly' ? 'border-primary bg-primary' : 'border-white/30'
                    }`}>
                      {selectedTier === 'monthly' && <Check className="h-3 w-3 text-primary-foreground" />}
                    </div>
                    <div>
                      <h3 className="text-base font-bold text-foreground">Monthly</h3>
                      <p className="text-xs text-muted-foreground">Billed monthly</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-xl font-bold text-foreground">
                      {currency.symbol}{monthlyPrice.toFixed(2)}
                      <span className="text-sm font-normal text-muted-foreground">/mo</span>
                    </div>
                  </div>
                </div>
              </button>
            </div>

            {/* Features */}
            <div className="rounded-2xl bg-white/5 backdrop-blur-xl border border-white/10 p-4 space-y-3">
              <h4 className="text-sm font-semibold text-foreground">What's included:</h4>
              <div className="grid grid-cols-1 gap-2">
                {[
                  'Unlimited access to all stories',
                  'New releases every month',
                  'Up to 5 profiles',
                  'Read on any device',
                ].map((feature) => (
                  <div key={feature} className="flex items-center gap-2 text-sm text-foreground/80">
                    <div className="w-4 h-4 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                      <Check className="h-2.5 w-2.5 text-primary" />
                    </div>
                    <span>{feature}</span>
                  </div>
                ))}
                {/* Custom book - annual only (hidden when hideCustomBook is true) */}
                {!hideCustomBook && (
                  <>
                    <div className={`flex items-center gap-2 text-sm ${selectedTier === 'annual' ? 'text-foreground/80' : 'text-muted-foreground/50'}`}>
                      <div className={`w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 ${selectedTier === 'annual' ? 'bg-primary/20' : 'bg-white/5'}`}>
                        {selectedTier === 'annual' ? (
                          <Check className="h-2.5 w-2.5 text-primary" />
                        ) : (
                          <X className="h-2.5 w-2.5 text-muted-foreground/50" />
                        )}
                      </div>
                      <span className={selectedTier === 'monthly' ? 'line-through' : ''}>
                        Custom book with your child as the hero
                      </span>
                    </div>
                    {selectedTier === 'monthly' && (
                      <div className="text-center mt-1">
                        <span className="text-[10px] text-primary/80">Annual only</span>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>

            {/* Total */}
            <div className="rounded-2xl bg-white/5 backdrop-blur-xl border border-white/10 p-4">
              {selectedTier === 'annual' && (
                <div className="text-center mb-2">
                  <span className="text-sm font-medium text-primary">{currency.symbol}{monthlyEquivalent}/month</span>
                </div>
              )}
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Total</span>
                <div className="text-right">
                  <span className="text-lg font-bold text-foreground">
                    {currency.symbol}{currentPrice.toFixed(2)}
                  </span>
                  <span className="text-sm text-muted-foreground">
                    /{selectedTier === 'annual' ? 'year' : 'month'}
                  </span>
                </div>
              </div>
            </div>

            {/* Payment Buttons */}
            <div className="space-y-3 pt-2">
              {/* Apple/Google Pay Button */}
              {!paymentMethodsReady ? (
                <div className="h-12 rounded-2xl bg-white/5 backdrop-blur-xl animate-pulse flex items-center justify-center">
                  <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                </div>
              ) : canMakePayment && paymentRequest && PaymentRequestButton && StripeElements && stripeInstance ? (
                <StripeElements stripe={stripeInstance} options={{ locale: 'en' }}>
                  <PaymentRequestButton
                    options={{
                      paymentRequest,
                      style: {
                        paymentRequestButton: {
                          type: 'default',
                          theme: 'dark',
                          height: '48px',
                        },
                      },
                    }}
                    onClick={() => {
                      console.log('[OnboardingPayment] PaymentRequest button clicked');
                    }}
                  />
                </StripeElements>
              ) : null}

              {/* Credit Card Button */}
              <Button
                className="w-full h-12 rounded-xl bg-white/10 backdrop-blur-[20px] border border-white/20 hover:bg-white/20 text-foreground font-medium tracking-tight transition-all duration-300"
                onClick={() => setShowPaymentForm(true)}
                disabled={isProcessing || !stripeInstance}
              >
                {isProcessing ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <CreditCard className="mr-2 h-4 w-4" />
                )}
                Pay with Card
              </Button>
            </div>

            {/* Trust indicators */}
            <div className="text-center space-y-1 pt-2">
              <p className="text-xs text-muted-foreground">
                Cancel anytime. No long-term commitment.
              </p>
              <p className="text-xs text-muted-foreground/70">
                Your subscription remains active until the end of the billing period.
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );

  return (
    <Drawer open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DrawerContent className="h-[90vh] max-h-[90vh] border-t border-white/10 bg-background/80 backdrop-blur-2xl">
        {content}
      </DrawerContent>
    </Drawer>
  );
}
